import React, { useState } from 'react';
import '../../css/gardenPlanterStylings/PlotPlanterAutoPlantingPanelStyling.css';
import { runPlantingAlgorithm } from '../../gardenAlgorithms/index';
import CustomDropdown from "../reusableComponents/CustomDropdown";

const PRESET_OPTIONS = [
    { value: 'user', label: 'Use user selected plants' },
    { value: 'random', label: 'Use random plants' },
    // { value: 'vegetable', label: 'Use vegetables only' },
    // { value: 'root', label: 'Use roots only' },
    // { value: 'flower', label: 'Use flowers only' },
    // { value: 'herb', label: 'Use herbs only' },
    { value: 'mix', label: 'Use a mix of everything' },
];

const PlotPlanterAutoPlantingPanel = ({ gardenInformation, userPlantMap, algorithmPreview, handleClear}) => {

    const [selectedMode, setSelectedMode] = useState('maximizeYield'); // selected algorithm (1st panel)
    const [plantMap, setPlantMap] = useState([]); // like a copy, to show the user garden plan after algo
    const [undoPlantMap, setUndoPlantMap] = useState(userPlantMap); // to revert to before the algo ran to retry for users
    const [plantCounts, setPlantCounts] = useState([]); // to update the counts after the algo runs
    const previousPlantings = []; // need to figure out previous plantings at some point

    const [presetOption, setPresetOption] = useState("user");

    const handlePresetChange = React.useCallback((preset) => {
        setPresetOption(preset.value);
    }, []);

    const [checkboxStates, setCheckboxStates] = useState({
        onlyUserSelected: false,
        strictCounts: false,
        useFullSpace: false,
        groupByPlantType: false,
        allowInterplanting: false,
        autoFillEmpty: false,
        allowVerticalGrowth: false,
        matchClimate: false,
        useFrostDates: false,
        rotateBasedOnPreviousSeason: false,
        preferHeatTolerant: false,
        filterByZone: false,
        prioritizePollinators: false
    });

    const [numericInputs, setNumericInputs] = useState({
        pestControlRatio: 20, // default 20%
        minSpacing: 2,        // default 2 units
        maxSpacing: 8,        // default 8 units
        plantingVariance: 20,
        pollinatorRatio: 30,
        diversityFactor: 70,
        clusterSize: 3,
        goodCompanionMultiplier: 10,
        moderateCompanionMultiplier: 2,
        badCompanionMultiplier: 15,
        maximizeYieldPlantingVariance: 0.2,
    });

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setCheckboxStates(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleNumericInputChange = (e) => {
        const { name, value } = e.target;
        setNumericInputs(prev => ({
            ...prev,
            [name]: value === '' ? '' : Number(value)
        }));
    };

    const handleAutoPlantingAlgorithm = (data) => {
        setPlantMap(data);
        // algorithmPreview(plantMap);
        algorithmPreview(data);
    }

    function normalizeArray(dataString) {
        let array = dataString.trim().split("\n").map(row => row.split(",").map(Number));
        array = array.filter(row => row.some(value => value !== 0));
        let transposed = array[0].map((_, colIndex) => array.map(row => row[colIndex]));
        transposed = transposed.filter(col => col.some(value => value !== 0));
        return transposed[0].map((_, rowIndex) => transposed.map(col => col[rowIndex]));
    }

    const handleAutoPlanting = async (algorithmType) => {
        setUndoPlantMap(userPlantMap);
        const normalizedMatrix = normalizeArray(gardenInformation.gardenInfo.matrix);
        // console.log(gardenInformation.plants);
        const result = await runPlantingAlgorithm({
            algorithmType,
            gardenMatrix: normalizedMatrix,
            plants: gardenInformation.plants,
            currentPlantMap: userPlantMap,
            config: {
                onlyUserSelected: checkboxStates.onlyUserSelected,
                strictCounts: checkboxStates.strictCounts,
                useFullSpace: checkboxStates.useFullSpace,
                groupByPlantType: checkboxStates.groupByPlantType,
                allowInterplanting: checkboxStates.allowInterplanting,
                autoFillEmpty: checkboxStates.autoFillEmpty,
                allowVerticalGrowth: checkboxStates.allowVerticalGrowth,
                prioritizePollinators: checkboxStates.prioritizePollinators,
                pestControlRatio: numericInputs.pestControlRatio,
                minSpacing: numericInputs.minSpacing,
                maxSpacing: numericInputs.maxSpacing,
                plantingVariance: numericInputs.plantingVariance,
                pollinatorRatio: numericInputs.pollinatorRatio,
                diversityFactor: numericInputs.diversityFactor,
                clusterSize: numericInputs.clusterSize,
                goodCompanionMultiplier: numericInputs.goodCompanionMultiplier,
                moderateCompanionMultiplier: numericInputs.moderateCompanionMultiplier,
                badCompanionMultiplier: numericInputs.badCompanionMultiplier,
                maximizeYieldPlantingVariance: numericInputs.maximizeYieldPlantingVariance
            },
            personalizedOptions: {
                matchClimate: checkboxStates.matchClimate,
                useFrostDates: checkboxStates.useFrostDates,
                rotateBasedOnPreviousSeason: checkboxStates.rotateBasedOnPreviousSeason,
                preferHeatTolerant: checkboxStates.preferHeatTolerant,
                filterByZone: checkboxStates.filterByZone
            },
            climateData: {
                zone: gardenInformation.hardiness,
            },
            previousSeasonData: previousPlantings || []
        });

        if (result.success) {
            // we update the plant map and counts!
            handleAutoPlantingAlgorithm(result.plantMap);
            setPlantCounts(result.metadata.unusedPlants);

            // show any warnings
            if (result.metadata.warnings.length > 0) {
                console.warn('Algorithm warnings:', result.metadata.warnings);
            }
        } else {
            console.error('Algorithm failed:', result.error);
        }
    };

    const handleClearAction = () => {
        handleClear();
    }


    return (
        <div className={`auto-planting-panel`}>
            <div className={`auto-planting-panel-body`}>
                {/*<div className={`auto-planting-panel-header`}>*/}
                {/*    <p>Auto Planting Options</p>*/}
                {/*</div>*/}

                <div className={`auto-planting-panel-options-section`}>
                    <div className={`auto-planting-panel-section`}>
                        <div className="auto-planting-panel-section-inner">
                            <div className={`auto-planting-panel-section-title`}>
                                <p>1. Select Algorithm Style</p>
                            </div>

                            <div className={`auto-planting-panel-section-divider`}></div>

                            <div className={`auto-planting-panel-modes-section`}>
                                <div
                                    className={`auto-planting-mode ${selectedMode === `maximizeYield` && ('selected-mode')}`}
                                    onClick={() => setSelectedMode('maximizeYield')}
                                >
                                    <div className={`auto-planting-mode-title`}>Maximize Yield</div>
                                    <div className={`auto-planting-mode-description`}>
                                        <span className={`highlight-green`}>Goal:</span> Pack the most plants possible.
                                    </div>
                                </div>

                                <div
                                    className={`auto-planting-mode ${selectedMode === `companionPlanting` && ('selected-mode')}`}
                                    onClick={() => setSelectedMode('companionPlanting')}
                                >
                                    <div className={`auto-planting-mode-title`}>Companion Planting</div>
                                    <div className={`auto-planting-mode-description`}>
                                        <span className={`highlight-green`}>Goal:</span> Plant veggies that benefit each
                                        other close together.
                                    </div>
                                </div>

                                {/*<div*/}
                                {/*    className={`auto-planting-mode ${selectedMode === `plantRotation` && ('selected-mode')}`}*/}
                                {/*    onClick={() => setSelectedMode('plantRotation')}*/}
                                {/*>*/}
                                {/*    <div className={`auto-planting-mode-title`}>Plant Rotation Strategy</div>*/}
                                {/*    <div className={`auto-planting-mode-description`}>*/}
                                {/*        <span className={`highlight-green`}>Goal:</span> Avoid planting the same crop in*/}
                                {/*        the same place as last season.*/}
                                {/*    </div>*/}
                                {/*</div>*/}

                                {/*<div*/}
                                {/*    className={`auto-planting-mode ${selectedMode === `successionPlanting` && ('selected-mode')}`}*/}
                                {/*    onClick={() => setSelectedMode('successionPlanting')}*/}
                                {/*>*/}
                                {/*    <div className={`auto-planting-mode-title`}>Succession Planting</div>*/}
                                {/*    <div className={`auto-planting-mode-description`}>*/}
                                {/*        <span className={`highlight-green`}>Goal:</span> Plant fast-growing crops in*/}
                                {/*        gaps so more crops can be harvested in waves.*/}
                                {/*    </div>*/}
                                {/*</div>*/}

                                <div
                                    className={`auto-planting-mode ${selectedMode === `pollinatorFriendly` && ('selected-mode')}`}
                                    onClick={() => setSelectedMode('pollinatorFriendly')}
                                >
                                    <div className={`auto-planting-mode-title`}>Pollinator Friendly</div>
                                    <div className={`auto-planting-mode-description`}>
                                        <span className={`highlight-green`}>Goal:</span> Mix in some flowering or
                                        bee-attracting veggies/herbs to attract pollinators.
                                    </div>
                                </div>

                                <div
                                    className={`auto-planting-mode ${selectedMode === `waterEfficiency` && ('selected-mode')}`}
                                    onClick={() => setSelectedMode('waterEfficiency')}
                                >
                                    <div className={`auto-planting-mode-title`}>Water Efficiency</div>
                                    <div className={`auto-planting-mode-description`}>
                                        <span className={`highlight-green`}>Goal:</span> Group plants by water needs
                                        (hydrozoning) to optimize irrigation.
                                    </div>
                                </div>

                                <div
                                    className={`auto-planting-mode ${selectedMode === `organicPestControl` && ('selected-mode')}`}
                                    onClick={() => setSelectedMode('organicPestControl')}
                                >
                                    <div className={`auto-planting-mode-title`}>Organic Pest Control</div>
                                    <div className={`auto-planting-mode-description`}>
                                        <span className={`highlight-green`}>Goal:</span> Use natural repellent
                                        combinations
                                        (e.g., marigolds with tomatoes) to reduce pest issues.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={`auto-planting-panel-section`}>
                        <div className={`auto-planting-panel-section-inner`}>
                            <div className={`auto-planting-panel-section-title`}>
                                <p>2. General configurations</p>
                            </div>

                            <div className={`auto-planting-panel-section-divider`}></div>

                            <div className="auto-planting-algo-config-options">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="autoFillEmpty"
                                        checked={checkboxStates.autoFillEmpty}
                                        onChange={handleCheckboxChange}
                                    />
                                    Auto-fill remaining empty plots
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        name="onlyUserSelected"
                                        checked={checkboxStates.onlyUserSelected}
                                        onChange={handleCheckboxChange}
                                    />
                                    Only plant user selected veggies
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        name="strictCounts"
                                        checked={checkboxStates.strictCounts}
                                        onChange={handleCheckboxChange}
                                    />
                                    Use strict veggie counts
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        name="useFullSpace"
                                        checked={checkboxStates.useFullSpace}
                                        onChange={handleCheckboxChange}
                                    />
                                    Use full garden space
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        name="groupByPlantType"
                                        checked={checkboxStates.groupByPlantType}
                                        onChange={handleCheckboxChange}
                                        disabled={true} // until we implement it
                                    />
                                    Group by plant type
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        name="allowInterplanting"
                                        checked={checkboxStates.allowInterplanting}
                                        onChange={handleCheckboxChange}
                                        disabled={true} // until we implement it
                                    />
                                    Allow interplanting
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        name="allowVerticalGrowth"
                                        checked={checkboxStates.allowVerticalGrowth}
                                        onChange={handleCheckboxChange}
                                        disabled={true} // until we implement it
                                    />
                                    Allow vertical growing potential
                                </label>
                            </div>
                        </div>
                    </div>

                    {/*<div className={`auto-planting-panel-section`}>*/}
                    {/*    <div className={`auto-planting-panel-section-inner`}>*/}
                    {/*        <div className={`auto-planting-panel-section-title`}>*/}
                    {/*            <p>3. Locality Options</p>*/}
                    {/*        </div>*/}

                    {/*        <div className={`auto-planting-panel-section-divider`}></div>*/}

                    {/*        <div className="auto-planting-smart-options">*/}
                    {/*            <label>*/}
                    {/*                <input*/}
                    {/*                    type="checkbox"*/}
                    {/*                    name="matchClimate"*/}
                    {/*                    checked={checkboxStates.matchClimate}*/}
                    {/*                    onChange={handleCheckboxChange}*/}
                    {/*                />*/}
                    {/*                Auto-select varieties that match climate*/}
                    {/*            </label>*/}
                    {/*            <label>*/}
                    {/*                <input*/}
                    {/*                    type="checkbox"*/}
                    {/*                    name="useFrostDates"*/}
                    {/*                    checked={checkboxStates.useFrostDates}*/}
                    {/*                    onChange={handleCheckboxChange}*/}
                    {/*                />*/}
                    {/*                Use frost dates for planning*/}
                    {/*            </label>*/}
                    {/*            <label>*/}
                    {/*                <input*/}
                    {/*                    type="checkbox"*/}
                    {/*                    name="rotateBasedOnPreviousSeason"*/}
                    {/*                    checked={checkboxStates.rotateBasedOnPreviousSeason}*/}
                    {/*                    onChange={handleCheckboxChange}*/}
                    {/*                />*/}
                    {/*                Rotate based on previous season*/}
                    {/*            </label>*/}
                    {/*            <label>*/}
                    {/*                <input*/}
                    {/*                    type="checkbox"*/}
                    {/*                    name="preferHeatTolerant"*/}
                    {/*                    checked={checkboxStates.preferHeatTolerant}*/}
                    {/*                    onChange={handleCheckboxChange}*/}
                    {/*                />*/}
                    {/*                Prefer heat-tolerant varieties for peak season*/}
                    {/*            </label>*/}
                    {/*            <label>*/}
                    {/*                <input*/}
                    {/*                    type="checkbox"*/}
                    {/*                    name="filterByZone"*/}
                    {/*                    checked={checkboxStates.filterByZone}*/}
                    {/*                    onChange={handleCheckboxChange}*/}
                    {/*                />*/}
                    {/*                Filter out crops not viable in my USDA zone*/}
                    {/*            </label>*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*</div>*/}


                    <div className={`auto-planting-panel-section`}>
                        <div className={`auto-planting-panel-section-inner`}>
                            <div className={`auto-planting-panel-section-title`}>
                                <p>3. Algorithm Configurations</p>
                            </div>

                            <div className={`auto-planting-panel-section-divider`}></div>

                            <div className="auto-planting-smart-options">

                                {selectedMode === "maximizeYield" && (
                                    <label>
                                        Planting Variance
                                        <input
                                            className={"ratio-inputs-section"}
                                            type="number"
                                            name="maximizeYieldPlantingVariance"
                                            min="0"
                                            max="1"
                                            step="0.1"
                                            value={numericInputs.maximizeYieldPlantingVariance}
                                            onChange={handleNumericInputChange}
                                            placeholder="e.g., 20"
                                        />
                                    </label>
                                )}

                                {selectedMode === "companionPlanting" && (
                                    <div>
                                        <label>
                                            Good companion multiplier
                                            <input
                                                className={"ratio-inputs-section"}
                                                type="number"
                                                name="goodCompanionMultiplier"
                                                min="5"
                                                max="20"
                                                step="1"
                                                value={numericInputs.goodCompanionMultiplier}
                                                onChange={handleNumericInputChange}
                                                placeholder="e.g., 20"
                                            />
                                        </label>

                                        <label>
                                            Moderate companion multiplier
                                            <input
                                                className={"ratio-inputs-section"}
                                                type="number"
                                                name="moderateCompanionMultiplier"
                                                min="0"
                                                max="10"
                                                step="1"
                                                value={numericInputs.moderateCompanionMultiplier}
                                                onChange={handleNumericInputChange}
                                                placeholder="e.g., 20"
                                            />
                                        </label>

                                        <label>
                                            Bad companion multiplier
                                            <input
                                                className={"ratio-inputs-section"}
                                                type="number"
                                                name="badCompanionMultiplier"
                                                min="0"
                                                max="15"
                                                step="1"
                                                value={numericInputs.badCompanionMultiplier}
                                                onChange={handleNumericInputChange}
                                                placeholder="e.g., 20"
                                            />
                                        </label>
                                    </div>
                                )}

                                {selectedMode === "pollinatorFriendly" && (
                                    <div>
                                        <label>
                                            Pollinator Ratio
                                            <input
                                                className={"ratio-inputs-section"}
                                                type="number"
                                                name="pollinatorRatio"
                                                min="0"
                                                max="100"
                                                step="1"
                                                value={numericInputs.pollinatorRatio}
                                                onChange={handleNumericInputChange}
                                                placeholder="e.g., 20"
                                            />
                                        </label>

                                        <label>
                                            Diversity Factor
                                            <input
                                                className={"ratio-inputs-section"}
                                                type="number"
                                                name="diversityFactor"
                                                min="0"
                                                max="100"
                                                step="1"
                                                value={numericInputs.diversityFactor}
                                                onChange={handleNumericInputChange}
                                                placeholder="e.g., 20"
                                            />
                                        </label>

                                        <label>
                                            Cluster Size
                                            <input
                                                className={"ratio-inputs-section"}
                                                type="number"
                                                name="clusterSize"
                                                min="0"
                                                max="10"
                                                step="1"
                                                value={numericInputs.clusterSize}
                                                onChange={handleNumericInputChange}
                                                placeholder="e.g., 2"
                                            />
                                        </label>

                                        <label>
                                            <input
                                                type="checkbox"
                                                name="prioritizePollinators"
                                                checked={checkboxStates.prioritizePollinators}
                                                onChange={handleCheckboxChange}
                                            />
                                            Prioritize Pollinators
                                        </label>
                                    </div>
                                )}

                                {selectedMode === "waterEfficiency" && (
                                    <label>
                                        Planting Variance
                                        <input
                                            className={"ratio-inputs-section"}
                                            type="number"
                                            name="plantingVariance"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={numericInputs.plantingVariance}
                                            onChange={handleNumericInputChange}
                                            placeholder="e.g., 20"
                                        />
                                    </label>
                                )}

                                {selectedMode === "organicPestControl" && (
                                    <div>
                                        <label>
                                            Pest Control Ratio
                                            <input
                                                className={"ratio-inputs-section"}
                                                type="number"
                                                name="pestControlRatio"
                                                min="0"
                                                max="100"
                                                step="1"
                                                value={numericInputs.pestControlRatio}
                                                onChange={handleNumericInputChange}
                                                placeholder="e.g., 20"
                                            />
                                        </label>

                                        <label>
                                            Min. space between pest control plants
                                            <input
                                                className={"numeric-inputs-section"}
                                                type="number"
                                                name="minSpacing"
                                                min="0"
                                                step="1"
                                                value={numericInputs.minSpacing}
                                                onChange={handleNumericInputChange}
                                                placeholder="e.g., 2"
                                            />
                                        </label>

                                        <label>
                                            Max. space between pest control plants
                                            <input
                                                className={"numeric-inputs-section"}
                                                type="number"
                                                name="maxSpacing"
                                                min="0"
                                                step="1"
                                                value={numericInputs.maxSpacing}
                                                onChange={handleNumericInputChange}
                                                placeholder="e.g., 8"
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>


                    <div className={`auto-planting-panel-section`}>
                        <div className={`auto-planting-panel-section-inner`}>
                            <div className={`auto-planting-panel-section-title`}>
                                <p>4. Use/Save Presets</p>
                            </div>

                            <div className={`auto-planting-panel-section-divider`}></div>

                            <div className="auto-planting-final-subgroup">
                                <div className="auto-planting-saved-presets">
                                    <label htmlFor="preset-dropdown">Select Plant Preset:</label>
                                    <CustomDropdown
                                        options={PRESET_OPTIONS}
                                        value={presetOption}
                                        onChange={handlePresetChange}
                                        placeholder="Select veggie prest..."
                                        className="garden-dropdown-custom"
                                    />
                                    {/*<select id="preset-dropdown" className="preset-dropdown">*/}
                                    {/*    <option value="spring-max-yield">Use user selected veggies</option>*/}
                                    {/*    <option value="spring-max-yield">Spring - Early Bloom Mix</option>*/}
                                    {/*    <option value="pollinator-mix">Pollinator Mix</option>*/}
                                    {/*</select>*/}

                                    {/*<div className="preset-buttons">*/}
                                    {/*    <button className="save-preset-button">Save Current as Preset</button>*/}
                                    {/*    <button className="rename-preset-button">Rename</button>*/}
                                    {/*    <button className="delete-preset-button danger">Delete</button>*/}
                                    {/*</div>*/}
                                </div>

                                <div className={`auto-planting-action-buttons`}>
                                    <button className={`auto-planting-footer-button`}
                                            onClick={() => handleClearAction()}>Clear
                                    </button>
                                    <button className={`auto-planting-footer-button`}
                                            onClick={() => handleAutoPlanting(selectedMode)}>Plant
                                    </button>

                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                {/*<div className={`auto-planting-footer-buttons`}>*/}
                {/*    <button className={`auto-planting-footer-button`}>Undo</button>*/}
                {/*    <button className={`auto-planting-footer-button`}>Preview</button>*/}
                {/*    <button className={`auto-planting-footer-button`}>Plant</button>*/}
                {/*</div>*/}
            </div>
        </div>
    )
}

export default PlotPlanterAutoPlantingPanel;